import { useReducer, useEffect, useContext, DependencyList } from 'react';
import Context from '../../containers/App/MainProvider/context';
import { ApiState, Action } from '../../types';

function useServiceReducer<T, U, V>(
  func: (params: U) => Promise<T>,
  reducer: (state: ApiState<T, U>, action: Action<T, U> | V) => ApiState<T, U>,
  init: U,
  extraReloadDeps: DependencyList = []
): [
  T | null,
  boolean,
  U,
  React.Dispatch<Action<T, U> | V>,
  (val: boolean) => void,
  (f?: U) => void
] {
  const mainContext = useContext(Context);

  const [{ loading, data, filter, trigger }, dispatch] = useReducer(reducer, {
    loading: false,
    data: null,
    filter: init,
    trigger: false,
  });

  const loader = (value: boolean) => dispatch({ type: 'LOADER', value });
  const reload = (f?: U) => dispatch({ type: 'RELOAD', filter: f });

  useEffect(() => {
    let didCancel = false;

    const fetchData = async () => {
      dispatch({ type: 'INIT' });
      try {
        const response = await func(filter);
        if (!didCancel) {
          dispatch({ type: 'SUCCESS', payload: response });
        }
      } catch (error) {
        if (!didCancel) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mainContext.notifyError(error as any);
          dispatch({ type: 'FAIL' });
        }
      }
    };

    fetchData();
    return () => {
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, ...extraReloadDeps]);

  return [data, loading, filter, dispatch, loader, reload];
}

export default useServiceReducer;
